import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SignOut, User, CaretDown } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

/**
 * Menú de perfil + logout en la barra oscura superior. Muestra iniciales
 * del usuario en un avatar circular y despliega un pequeño menú al hacer click.
 */
export default function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user) return null;

  const nombre = user.nombre || user.username || user.email || "Usuario";
  const iniciales = nombre.split(" ").filter(Boolean).slice(0, 2)
    .map((s) => s[0]?.toUpperCase()).join("") || "U";

  const doLogout = async () => {
    setOpen(false);
    try { await logout(); } catch { /* noop */ }
    navigate("/login");
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="user-menu">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 h-8 px-2 rounded-md hover:bg-white/10 transition-colors text-white"
        data-testid="user-menu-trigger"
      >
        <div className="w-6 h-6 rounded-full bg-[#921A0E] text-white text-[10px] font-bold flex items-center justify-center">
          {iniciales}
        </div>
        <div className="hidden md:block text-xs font-medium max-w-[120px] truncate">{nombre}</div>
        <CaretDown size={10} className="text-neutral-400" />
      </button>

      {open && (
        <div className="absolute z-50 top-full right-0 mt-1 w-64 bg-white text-neutral-900 border border-neutral-300 rounded-md shadow-xl overflow-hidden">
          <div className="px-4 py-3 bg-gradient-to-r from-neutral-800 to-neutral-900 text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#921A0E] text-white text-sm font-bold flex items-center justify-center shrink-0">
                {iniciales}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{nombre}</div>
                {user.email && <div className="text-[11px] text-neutral-300 truncate">{user.email}</div>}
                {user.rol && (
                  <div className="text-[10px] uppercase font-mono text-neutral-400 mt-0.5">{user.rol}</div>
                )}
              </div>
            </div>
          </div>
          <div className="py-1">
            <button
              type="button"
              onClick={() => { setOpen(false); navigate("/perfil"); }}
              className="w-full text-left px-4 py-2 text-sm hover:bg-neutral-50 flex items-center gap-2"
              data-testid="user-menu-profile"
            >
              <User size={14} weight="duotone" className="text-neutral-500" />
              Mi perfil
            </button>
            <div className="h-px bg-neutral-200 mx-3 my-1" />
            <button
              type="button"
              onClick={doLogout}
              className="w-full text-left px-4 py-2 text-sm hover:bg-rose-50 text-rose-700 flex items-center gap-2"
              data-testid="user-menu-logout"
            >
              <SignOut size={14} weight="bold" />
              Cerrar sesión
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
